import { useTranslation } from 'react-i18next'

import { useCreditInfo } from '@/lib/hooks'
import { isFairgenInternalTier } from '@/lib/billing/tier'
import { CreditIcon } from '@/components/icons/CreditIcon'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { intlLocaleForUi } from '@/i18n'

interface CreditWalletBadgeProps {
  tier: string | null | undefined
}

/**
 * Sidebar balance: `available` (balance minus credits set aside for running
 * social updates). When some are set aside, a tooltip says how many on hover,
 * and the same sentence sits in visually hidden text: the badge lives inside
 * the billing menu item, where a nested tab stop is unreachable (the menu
 * moves focus by arrow keys) and would be a nested interactive control, so
 * the explanation joins the menu item's accessible name instead.
 */
export function CreditWalletBadge({ tier }: CreditWalletBadgeProps) {
  const { data: creditData, isLoading } = useCreditInfo()
  const { t, i18n } = useTranslation('app')

  if (isLoading || !creditData) {
    return null
  }

  const numberFormat = new Intl.NumberFormat(intlLocaleForUi(i18n.language))
  const isInternal = isFairgenInternalTier(tier)
  // Past the plan the pool is exhausted: `available` (balance minus credits set aside) can go
  // negative, which is not a balance. Show the over-plan state instead of a minus number.
  const isOver = !isInternal && creditData.isInOverage === true
  const display = isInternal
    ? 'Unlimited'
    : isOver
      ? t('billing.wallet.overPlan')
      : numberFormat.format(Math.max(0, creditData.available))
  const pending = Number.isFinite(creditData.pending) ? creditData.pending : 0
  const hasHeld = !isInternal && !isOver && pending > 0
  const heldText = t('billing.wallet.held', { count: pending, value: numberFormat.format(pending) })

  const badge = (
    <span
      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-muted/60 dark:bg-muted/40 px-2 py-0.5 text-[14px] font-medium text-muted-foreground"
      data-testid="sidebar-credit-wallet"
      title={isOver ? display : undefined}
    >
      <CreditIcon className={`h-3.5 w-3.5 shrink-0${isOver ? ' text-destructive' : ''}`} strokeWidth={1} />
      {isOver ? (
        // Compact so the menu label keeps its line: icon only, the text goes to assistive tech.
        <span className="sr-only">{display}</span>
      ) : (
        <span className="tabular-nums">{display}</span>
      )}
      {hasHeld ? (
        <span className="sr-only" data-testid="sidebar-credit-wallet-held-sr">
          {heldText}
        </span>
      ) : null}
    </span>
  )

  if (!hasHeld) {
    return badge
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent className="max-w-[220px] text-xs" data-testid="sidebar-credit-wallet-held">
          {heldText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
